 import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, UserCheck } from 'lucide-react';
import apiClient from '../services/api';

const EmploymentStatuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    statusid: null,
    statuscode: '',
    statusname: '',
    description: '',
    isactive: true
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    filterStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statuses]);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/EmploymentStatuses.php');
      
      if (response.data.success) {
        setStatuses(response.data.data || []);
      } else {
        alert('Failed to fetch employment statuses: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching employment statuses:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching employment statuses. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterStatuses = () => {
    if (!searchTerm.trim()) {
      setFilteredStatuses(statuses);
    } else {
      const filtered = statuses.filter(status =>
        status.statusname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.statuscode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (status.description && status.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredStatuses(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.statuscode.trim()) {
      newErrors.statuscode = 'Status code is required';
    } else if (formData.statuscode.length > 20) {
      newErrors.statuscode = 'Status code must be 20 characters or less';
    } else if (!/^[A-Z0-9_]+$/.test(formData.statuscode)) {
      newErrors.statuscode = 'Status code must contain only uppercase letters, numbers, and underscores';
    }
    
    if (!formData.statusname.trim()) {
      newErrors.statusname = 'Status name is required';
    } else if (formData.statusname.length > 50) {
      newErrors.statusname = 'Status name must be 50 characters or less';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/EmploymentStatuses.php', {
        ...formData,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Employment status updated successfully!' : 'Employment status created successfully!');
        setShowModal(false);
        resetForm();
        fetchStatuses();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving employment status:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error saving employment status. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (status) => {
    setFormData({
      statusid: status.statusid,
      statuscode: status.statuscode,
      statusname: status.statusname,
      description: status.description || '',
      isactive: status.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id, statusname) => {
    if (!window.confirm(`Are you sure you want to delete "${statusname}"?`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/EmploymentStatuses.php', {
        data: { statusid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Employment status deleted successfully!');
        fetchStatuses();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting employment status:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.data?.message) {
        alert('Error: ' + error.response.data.message);
      } else {
        alert('Error deleting employment status. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      statusid: null,
      statuscode: '',
      statusname: '',
      description: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleStatusCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/\s/g, '_');
    setFormData({ ...formData, statuscode: value });
  };

  return (
    <div className="master-container" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div className="master-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div className="header-left">
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1f2937' }}>Employment Statuses Master</h2>
          <p className="subtitle" style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Manage employee status classifications</p>
        </div>
        <button 
          className="btn-primary" 
          onClick={openAddModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          <Plus size={20} />
          Add Status
        </button>
      </div>

      <div className="master-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div className="search-box" style={{ position: 'relative', width: '400px' }}>
          <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search by status code, name, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 40px 10px 40px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          {searchTerm && (
            <button 
              className="btn-clear" 
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#6b7280'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info" style={{ fontSize: '14px', color: '#6b7280' }}>
          Total: {filteredStatuses.length} {filteredStatuses.length === 1 ? 'status' : 'statuses'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Loading employment statuses...
        </div>
      ) : filteredStatuses.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px' }}>
          <UserCheck size={48} style={{ color: '#d1d5db', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>No employment statuses found</p>
          {searchTerm ? (
            <button onClick={() => setSearchTerm('')} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>
              Clear search
            </button>
          ) : (
            <button onClick={openAddModal} style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Add your first employment status
            </button>
          )}
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Status Code</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Status Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStatuses.map((status) => (
                <tr key={status.statusid} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '14px' }}>{status.statuscode}</td>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: 500 }}>{status.statusname}</td>
                  <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{status.description || '-'}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: status.isactive ? '#d1fae5' : '#fee2e2',
                      color: status.isactive ? '#065f46' : '#991b1b'
                    }}>
                      {status.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleEdit(status)}
                      title="Edit"
                      style={{
                        padding: '6px',
                        marginRight: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: 'pointer',
                        color: '#3b82f6'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(status.statusid, status.statusname)}
                      title="Delete"
                      style={{
                        padding: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: 'pointer',
                        color: '#ef4444'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div 
          className="modal-overlay" 
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div 
              className="modal-header" 
              style={{
                padding: '20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                {editMode ? 'Edit Employment Status' : 'Add New Employment Status'}
              </h3>
              <button 
                className="btn-close" 
                onClick={closeModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280'
                }}
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="statuscode" style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                  Status Code <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="statuscode"
                  value={formData.statuscode}
                  onChange={handleStatusCodeChange}
                  maxLength={20}
                  placeholder="e.g., ACTIVE, PROBATION, RESIGNED"
                  disabled={editMode}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.statuscode ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: editMode ? '#f3f4f6' : 'white'
                  }}
                />
                {errors.statuscode && (
                  <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#ef4444' }}>
                    {errors.statuscode}
                  </span>
                )}
                <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  Uppercase letters, numbers, and underscores only
                </span>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="statusname" style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                  Status Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="statusname"
                  value={formData.statusname}
                  onChange={(e) => setFormData({ ...formData, statusname: e.target.value })}
                  maxLength={50}
                  placeholder="e.g., Active, Probation, Resigned"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.statusname ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {errors.statusname && (
                  <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#ef4444' }}>
                    {errors.statusname}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="description" style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  maxLength={500}
                  rows={3}
                  placeholder="Optional description of this employment status"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.description ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                {errors.description && (
                  <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#ef4444' }}>
                    {errors.description}
                  </span>
                )}
                <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                  {formData.description.length}/500 characters
                </span>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                    style={{ marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={submitLoading}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    opacity: submitLoading ? 0.5 : 1
                  }}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmploymentStatuses;